import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import AlertsIcon from "@material-ui/icons/ReportProblemOutlined";
import DoneIcon from "@material-ui/icons/DoneOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import useMenuStyles from "../../components/menuStyles";

// Tela "Alertas": tudo que deu errado na conta, num lugar só, com filtros.
const TYPE_LABEL = {
  msg_sem_destino: "Mensagem sem destino",
  msg_recusada: "Mensagem recusada pelo WhatsApp",
  chamada_sem_destino: "Chamada sem destino",
  agente_erro: "Agente com erro",
  fluxo_falhou: "Fluxo falhou",
  whatsapp_desconectado: "WhatsApp desconectado",
  whatsapp_sem_conexao: "WhatsApp sem conexão",
};
const PERIODS = [
  { key: "", label: "Qualquer período" },
  { key: "24h", label: "Últimas 24h", ms: 24 * 3600e3 },
  { key: "7d", label: "Últimos 7 dias", ms: 7 * 24 * 3600e3 },
  { key: "30d", label: "Últimos 30 dias", ms: 30 * 24 * 3600e3 },
];

const useStyles = makeStyles(theme => {
  const t = k => (theme.app ? theme.app[k] : undefined);
  return {
    wrap: { padding: "28px 32px", maxWidth: 1000, margin: "0 auto" },
    head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 6 },
    title: { fontSize: 22, fontWeight: 800, color: t("heading") || "#1B222A" },
    spacer: { marginLeft: "auto" },
    desc: { fontSize: 13.5, color: t("textTertiary") || "#929AA4", marginBottom: 18 },
    filters: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 },
    fsel: { minWidth: 170 },
    list: { display: "flex", flexDirection: "column", gap: 10 },
    card: {
      display: "flex", gap: 14, alignItems: "flex-start", background: "#fff",
      border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 14, padding: "14px 16px",
      borderLeft: "4px solid transparent",
    },
    error: { borderLeftColor: "#D14343" },
    warn: { borderLeftColor: "#E0A008" },
    info: { borderLeftColor: "#5B6470" },
    readCard: { opacity: 0.55, borderLeftColor: `${t("border") || "#E6E9ED"} !important`, background: t("lineSoft") || "#F7F8FA" },
    right: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 },
    markBtn: { textTransform: "none", fontSize: 12.5, fontWeight: 600, color: "#0b7285", padding: "2px 8px", minWidth: 0, "& .MuiButton-startIcon": { marginRight: 4 }, "&:hover": { background: "rgba(11,114,133,.08)" } },
    lidoTag: { fontSize: 11.5, fontWeight: 700, color: "#5B6470", background: "#EEF1F4", borderRadius: 6, padding: "2px 8px" },
    unreadDot: { width: 8, height: 8, borderRadius: 999, background: "#0b7285", marginTop: 6, flexShrink: 0 },
    readDot: { width: 8, height: 8, borderRadius: 999, background: "transparent", marginTop: 6, flexShrink: 0 },
    main: { flex: 1, minWidth: 0 },
    ttl: { fontSize: 14.5, fontWeight: 600, color: t("ink") || "#2c3036" },
    detail: { fontSize: 13, color: t("textSecondary") || "#5B6470", marginTop: 3, wordBreak: "break-word" },
    chips: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 },
    chip: { fontSize: 11.5, fontWeight: 600, color: "#5B6470", background: "#EEF1F4", borderRadius: 6, padding: "2px 8px" },
    when: { fontSize: 12, color: t("textTertiary") || "#929AA4", whiteSpace: "nowrap", marginLeft: 8 },
    empty: {
      background: "#fff", border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 16,
      padding: theme.spacing(7, 3), textAlign: "center",
    },
    emptyIcon: {
      width: 46, height: 46, borderRadius: 999, background: t("lineSoft") || "#F1F4F2",
      display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
      "& svg": { fontSize: 22, color: t("textTertiary") || "#929AA4" },
    },
    emptyText: { color: t("textSecondary") || "#5B6470", fontSize: 13.5 },
  };
});

const Alerts = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const { projects } = useProject();
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connectors, setConnectors] = useState([]);
  const [filters, setFilters] = useState({ type: "", connectorId: "", projectId: "", period: "", unread: false });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.connectorId) params.connectorId = filters.connectorId;
      if (filters.projectId) params.projectId = filters.projectId;
      if (filters.unread) params.unread = "1";
      const p = PERIODS.find(x => x.key === filters.period);
      if (p && p.ms) params.since = new Date(Date.now() - p.ms).toISOString();
      const { data } = await api.get("/alerts", { params });
      setAlerts(data.alerts || []);
      setUnread(data.unread || 0);
    } catch (err) {
      toastError(err);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/connectors").then(({ data }) => setConnectors(data.connectors || [])).catch(() => undefined);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/alerts/read", {});
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // marca UM alerta como lido: otimista (esmaece na hora) + atualiza o contador.
  const markOne = async id => {
    setAlerts(prev => prev.map(a => (a.id === id ? { ...a, read: true } : a)));
    setUnread(u => Math.max(0, u - 1));
    try {
      const { data } = await api.post("/alerts/read", { ids: [id] });
      if (typeof data.unread === "number") setUnread(data.unread);
    } catch (err) {
      toastError(err);
      load();
    }
  };

  const sel = (label, value, onChange, options) => (
    <TextField
      select size="small" variant="outlined" label={label} value={value} onChange={e => onChange(e.target.value)}
      className={classes.fsel} SelectProps={{ MenuProps: { classes: { paper: menu.selectPaper }, getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" } } }}
    >
      {options.map(o => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  );

  return (
    <div className={classes.wrap}>
      <div className={classes.head}>
        <span className={classes.title}>Alertas</span>
        {unread > 0 ? <span className={classes.chip} style={{ background: "#FDECEC", color: "#D14343" }}>{unread} não lido(s)</span> : null}
        <span className={classes.spacer} />
        <Button size="small" onClick={markAll} disabled={!unread}>Marcar tudo como lido</Button>
      </div>
      <div className={classes.desc}>Tudo que deu errado na sua conta: mensagem ou chamada sem destino, agente com erro, fluxo que falhou.</div>

      <div className={classes.filters}>
        {sel("Tipo", filters.type, v => setFilters({ ...filters, type: v }), [{ value: "", label: "Todos os tipos" }, ...Object.keys(TYPE_LABEL).map(k => ({ value: k, label: TYPE_LABEL[k] }))])}
        {sel("Conector", filters.connectorId, v => setFilters({ ...filters, connectorId: v }), [{ value: "", label: "Todos os conectores" }, ...connectors.map(c => ({ value: c.id, label: c.name }))])}
        {sel("Projeto", filters.projectId, v => setFilters({ ...filters, projectId: v }), [{ value: "", label: "Todos os projetos" }, ...projects.map(p => ({ value: p.id, label: p.name }))])}
        {sel("Período", filters.period, v => setFilters({ ...filters, period: v }), PERIODS.map(p => ({ value: p.key, label: p.label })))}
        <FormControlLabel control={<Checkbox color="primary" checked={filters.unread} onChange={e => setFilters({ ...filters, unread: e.target.checked })} />} label="Só não lidos" />
      </div>

      {alerts.length === 0 ? (
        <div className={classes.empty}>
          <div className={classes.emptyIcon}><AlertsIcon /></div>
          <div className={classes.emptyText}>Nenhum alerta com esses filtros. Bom sinal.</div>
        </div>
      ) : (
        <div className={classes.list}>
          {alerts.map(a => (
            <div key={a.id} className={`${classes.card} ${classes[a.severity] || classes.error} ${a.read ? classes.readCard : ""}`}>
              <div className={a.read ? classes.readDot : classes.unreadDot} />
              <div className={classes.main}>
                <div className={classes.ttl}>{a.title}</div>
                {a.detail ? <div className={classes.detail}>{a.detail}</div> : null}
                <div className={classes.chips}>
                  <span className={classes.chip}>{TYPE_LABEL[a.type] || a.type}</span>
                  {a.connectorName ? <span className={classes.chip}>conector: {a.connectorName}</span> : null}
                  {a.applicationName ? <span className={classes.chip}>fluxo: {a.applicationName}</span> : null}
                  {a.projectName ? <span className={classes.chip}>projeto: {a.projectName}</span> : null}
                </div>
              </div>
              <div className={classes.right}>
                <span className={classes.when}>{new Date(a.at).toLocaleString("pt-BR")}</span>
                {!a.read ? (
                  <Button size="small" className={classes.markBtn} startIcon={<DoneIcon style={{ fontSize: 16 }} />} onClick={() => markOne(a.id)}>
                    Marcar como lido
                  </Button>
                ) : (
                  <span className={classes.lidoTag}>lido</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Alerts;
